import { Injectable, Provider } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, NativeDateAdapter } from '@angular/material/core';

/** Formato usado al escribir y mostrar la fecha dentro del campo */
const INPUT_FORMAT = 'dd/mm/aaaa';

/**
 * Adaptador de fechas que entiende lo que el usuario escribe como dd/mm/aaaa.
 *
 * El NativeDateAdapter de Material usa Date.parse, que interpreta "02/03/2026" en formato
 * estadounidense (3 de febrero). Este adaptador lo lee como 2 de marzo.
 * Sigue trabajando con objetos Date, así que el código que ya usa estas fechas no cambia.
 */
@Injectable()
export class PeruDateAdapter extends NativeDateAdapter {

    override parse(value: any, parseFormat?: any): Date | null {
        if (typeof value === 'number') {
            return new Date(value);
        }
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (value instanceof Date) {
            return value;
        }

        // Acepta dd/mm/aaaa, dd-mm-aaaa, dd.mm.aaaa, años de 2 dígitos (26 → 2026)
        // y la fecha escrita de corrido (02022025), por si el campo no tiene la máscara
        const texto = String(value).trim();
        const match = texto.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/)
            ?? texto.match(/^(\d{2})(\d{2})(\d{4})$/);
        if (!match) {
            // Cualquier otro texto se considera inválido en vez de adivinarlo en formato de EE. UU.
            return this.invalid();
        }

        const day = +match[1];
        const month = +match[2] - 1;
        const year = match[3].length === 2 ? 2000 + +match[3] : +match[3];

        if (month < 0 || month > 11 || day < 1 || day > this.getNumDaysInMonth(this.createDate(year, month, 1))) {
            return this.invalid();
        }
        return this.createDate(year, month, day);
    }

    override format(date: Date, displayFormat: any): string {
        if (displayFormat === INPUT_FORMAT) {
            if (!this.isValid(date)) {
                return '';
            }
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            return `${dd}/${mm}/${date.getFullYear()}`;
        }
        return super.format(date, displayFormat);
    }
}

export const PERU_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: INPUT_FORMAT,
    },
    display: {
        dateInput: INPUT_FORMAT,
        monthYearLabel: { year: 'numeric', month: 'short' },
        dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
        monthYearA11yLabel: { year: 'numeric', month: 'long' },
    },
};

/**
 * Providers para usar fechas en formato peruano (es-PE, dd/mm/aaaa) con escritura manual.
 * Se agregan en el `providers` de un componente o módulo.
 */
export const PERU_DATE_PROVIDERS: Provider[] = [
    { provide: MAT_DATE_LOCALE, useValue: 'es-PE' },
    { provide: DateAdapter, useClass: PeruDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PERU_DATE_FORMATS },
];
